import { useLocation } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { ShieldCheck, LogIn, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

export default function AdminLogin() {
  const location = useLocation();
  const error = location.state?.error;

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="min-h-screen text-white relative overflow-hidden flex items-center justify-center px-6 py-10"
      style={{ background: "linear-gradient(135deg, #061A3A 0%, #0a2754 60%, #0b3470 100%)" }}
      data-testid="admin-login"
      data-prerender-ready="true"
    >
      <Seo {...getSeoForPath("/admin/login")} />
      {/* Brand accents */}
      <div
        aria-hidden="true"
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: "rgba(0,114,255,0.20)", filter: "blur(120px)" }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -right-40 w-[560px] h-[560px] rounded-full pointer-events-none"
        style={{ background: "rgba(0,114,255,0.18)", filter: "blur(140px)" }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      <div className="w-full max-w-[460px] relative">
        {/* Branded header above card */}
        <div className="flex flex-col items-center text-center mb-7">
          <Logo
            variant="light"
            to="/"
            className="h-14 sm:h-16 w-auto select-none drop-shadow-[0_8px_24px_rgba(0,114,255,0.35)]"
            linkClassName="shrink-0"
            testId="admin-login-logo"
          />
          <div className="mt-5 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.08] border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF]">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
            Internal · Restricted Access
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white text-[var(--swipe-navy)] rounded-2xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.55)] p-8 sm:p-10 relative">
          {/* Accent bar */}
          <div
            aria-hidden="true"
            className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl"
            style={{ background: "linear-gradient(90deg, #0072FF 0%, #3A8EFF 60%, #061A3A 100%)" }}
          />

          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[var(--swipe-soft-blue)] mb-6">
            <ShieldCheck className="w-7 h-7 text-[var(--swipe-accent)]" />
          </div>

          <h1 className="font-display-tight text-[1.75rem] sm:text-[2rem] font-extrabold leading-[1.12]">
            Swipetronics Admin Portal
          </h1>
          <p className="mt-3 text-[14px] text-slate-600 leading-[1.7]">
            Manage leads, follow-ups, and campaign activity. Sign in with your authorized Swipetronics Google account.
          </p>

          {error ? (
            <div
              className="mt-5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px]"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          ) : null}

          <button
            type="button"
            onClick={handleLogin}
            data-testid="admin-google-signin"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-[var(--swipe-accent)] hover:bg-[#005ed1] text-white font-semibold text-[15px] transition-colors shadow-[0_10px_24px_-8px_rgba(0,114,255,0.55)]"
          >
            <LogIn className="w-4 h-4" />
            Continue with Google
          </button>

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-100" />
            <span className="text-[10.5px] uppercase tracking-[0.2em] font-bold text-slate-400">Secure access</span>
            <div className="h-px flex-1 bg-slate-100" />
          </div>

          <p className="mt-5 text-[11.5px] text-slate-500 text-center leading-[1.65]">
            Only authorized Swipetronics admin email addresses can access this dashboard.
            All other sign-in attempts are denied.
          </p>
        </div>

        <a
          href="/"
          className="mt-6 inline-flex items-center gap-1.5 text-[12.5px] text-white/65 hover:text-white transition-colors mx-auto justify-center w-full"
          data-testid="admin-login-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to swipetronics.com
        </a>
      </div>
    </div>
  );
}
